import { useMemo, useState } from 'react'

import { resourceTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  OWN_CATALOG_CAPABILITIES,
  OWN_PRICE_TAG,
  OWN_WINDOWS,
  OwnProductPricesScreen,
  ownDeletePrice,
  ownNomenclatureApi,
  ownPriceKinds,
  ownPrices,
  ownProduct,
  ownSetPrice,
  parsePriceInput,
  productPricesVm,
  todayIso,
  type OwnProductPricesActions,
  type PriceSetDraft,
} from '@krp/resource-own-ui'

import { useAppDispatch } from '../../app/hooks'
import { useOwnCapability } from './useOwnCapability'

/**
 * Binding окна «Цены товара своей базы» (RP-OWN3): товар и база — из адреса окна. Дата просмотра и черновик новой цены — пока
 * открыт экран; цены на дату и вся история — два запроса в кэше. Запись цены перечитывает оба.
 */
export function OwnProductPricesBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const productId = window.params.id ?? ''
  const canWrite = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.pricesWrite)
  const today = useMemo(() => todayIso(), [])
  const [on, setOn] = useState(today)
  const [draft, setDraft] = useState<PriceSetDraft>({ priceTypeId: '', validFrom: today, price: '' })
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const ready = resourceId !== '' && productId !== ''
  const productArgs = useMemo(() => (ready ? { resourceId, productId, on: null } : null), [productId, ready, resourceId])
  const currentArgs = useMemo(() => (ready && on !== '' ? { resourceId, productId, on } : null), [on, productId, ready, resourceId])
  const historyArgs = useMemo(() => (ready ? { resourceId, productId, on: null } : null), [productId, ready, resourceId])
  const kindArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const product = useQueryResult(ownProduct, productArgs)
  const current = useQueryResult(ownPrices, currentArgs)
  const history = useQueryResult(ownPrices, historyArgs)
  const kinds = useQueryResult(ownPriceKinds, kindArgs)
  const [setPrice, setState] = ownSetPrice.useMutation()
  const [deletePrice, deleteState] = ownDeletePrice.useMutation()

  const allKinds = useMemo(() => kinds.data ?? [], [kinds.data])
  // Единственный действующий вид выбран сразу: человеку не нужно выбирать из одного.
  const shown = useMemo(() => {
    const active = allKinds.filter((kind) => !kind.archived)
    return draft.priceTypeId === '' && active.length === 1 && active[0] !== undefined ? { ...draft, priceTypeId: active[0].id } : draft
  }, [allKinds, draft])

  const vm = productPricesVm({
    product,
    on,
    current,
    history,
    kinds: allKinds,
    draft: shown,
    command: setState,
    confirmDeleteKey,
    deleteCommand: deleteState,
    done,
    canWrite,
  })

  const actions = useMemo<OwnProductPricesActions>(
    () => ({
      onChanged: setOn,
      draftChanged: (next) => {
        setState.reset()
        setDraft(next)
      },
      save: () => {
        const price = parsePriceInput(shown.price)
        if (!price.ok || vm.form.problem !== null) return
        setDone(null)
        void setPrice({ resourceId, productId, priceTypeId: shown.priceTypeId, validFrom: shown.validFrom, kopecks: price.kopecks })
          .unwrap()
          .then(() => {
            const kind = allKinds.find((item) => item.id === shown.priceTypeId)
            setDone(`Цена «${kind?.name ?? 'вид цены'}» записана с ${shown.validFrom.split('-').reverse().join('.')}`)
            setDraft({ ...shown, price: '' })
          })
          .catch(() => undefined)
      },
      askDelete: (key) => {
        deleteState.reset()
        setDone(null)
        setConfirmDeleteKey(key)
      },
      cancelDelete: () => setConfirmDeleteKey(null),
      confirmDelete: () => {
        const [priceTypeId = '', validFrom = ''] = (confirmDeleteKey ?? '').split('|')
        if (priceTypeId === '' || validFrom === '') return
        void deletePrice({ resourceId, productId, priceTypeId, validFrom })
          .unwrap()
          .then(() => {
            setDone('Запись цены удалена')
            setConfirmDeleteKey(null)
          })
          .catch(() => setConfirmDeleteKey(null))
      },
      reload: () => dispatch(ownNomenclatureApi.util.invalidateTags([resourceTag(OWN_PRICE_TAG, resourceId, productId)])),
      openProduct: () => openWindow(OWN_WINDOWS.product, { rid: resourceId, id: productId }),
    }),
    [allKinds, confirmDeleteKey, deletePrice, deleteState, dispatch, openWindow, productId, resourceId, setPrice, setState, shown, vm.form.problem],
  )

  return <OwnProductPricesScreen vm={vm} actions={actions} />
}
